export const playerMatchmakerEventsURL = '/api/player/v1/matchmaker/events'

export const playerMatchmakerSignalReasons = [
  'ready_check_started',
  'ready_check_progress',
  'ready_check_ended',
  'match_connect_ready',
  'match_in_progress',
  'match_post_match',
  'match_ended',
] as const

export type PlayerMatchmakerSignalReason =
  | typeof playerMatchmakerSignalReasons[number]
  | 'snapshot'
  | 'resume'
  | 'visible'

export type PlayerMatchmakerSignal = {
  reason: PlayerMatchmakerSignalReason
  observedAt: number
}

type EventSourceLike = {
  onmessage: ((event: MessageEvent<string>) => void) | null
  onopen: ((event: Event) => void) | null
  onerror: ((event: Event) => void) | null
  readyState?: number
  close: () => void
}

type EventTargetLike = {
  addEventListener: (type: string, listener: EventListener) => void
  removeEventListener: (type: string, listener: EventListener) => void
}

type VisibilityTargetLike = EventTargetLike & {
  hidden: boolean
}

type PlayerMatchmakerSignalRuntimeOptions = {
  authenticated: () => boolean
  onSignal: (signal: PlayerMatchmakerSignal) => void
  createEventSource?: (url: string) => EventSourceLike
  pageTarget?: EventTargetLike
  visibilityTarget?: VisibilityTargetLike
  now?: () => number
  schedule?: (callback: () => void, delayMS: number) => unknown
  cancel?: (handle: unknown) => void
}

export type PlayerMatchmakerSignalRuntime = {
  start: () => void
  stop: () => void
  dispose: () => void
}

export function createPlayerMatchmakerSignalRuntime(
  options: PlayerMatchmakerSignalRuntimeOptions,
): PlayerMatchmakerSignalRuntime {
  const createEventSource = options.createEventSource ?? (url => new EventSource(url))
  const pageTarget = options.pageTarget ?? window
  const visibilityTarget = options.visibilityTarget ?? document
  const now = options.now ?? Date.now
  const schedule = options.schedule ?? ((callback, delayMS) => window.setTimeout(callback, delayMS))
  const cancel = options.cancel ?? (handle => window.clearTimeout(handle as number))
  let source: EventSourceLike | null = null
  let reopenTimer: unknown
  let reopenAttempt = 0
  let running = false
  let suspended = false
  let disposed = false

  const handleFreeze = () => {
    suspended = true
    closeSource()
  }
  const handleResume = () => {
    suspended = false
    if (!running || !options.authenticated()) return
    openSource()
    options.onSignal({ reason: 'resume', observedAt: now() })
  }
  const handlePageHide = () => {
    suspended = true
    closeSource()
  }
  const handlePageShow = () => {
    suspended = false
    if (!running || !options.authenticated()) return
    openSource()
    options.onSignal({ reason: 'resume', observedAt: now() })
  }
  const handleVisibilityChange = () => {
    if (!running || visibilityTarget.hidden || !options.authenticated()) return
    openSource()
    options.onSignal({ reason: 'visible', observedAt: now() })
  }

  visibilityTarget.addEventListener('freeze', handleFreeze)
  visibilityTarget.addEventListener('resume', handleResume)
  visibilityTarget.addEventListener('visibilitychange', handleVisibilityChange)
  pageTarget.addEventListener('pagehide', handlePageHide)
  pageTarget.addEventListener('pageshow', handlePageShow)

  function openSource(): void {
    if (
      disposed ||
      !running ||
      suspended ||
      source ||
      !options.authenticated()
    ) {
      return
    }
    try {
      const next = createEventSource(playerMatchmakerEventsURL)
      next.onopen = () => {
        if (source !== next) return
        reopenAttempt = 0
      }
      next.onerror = () => {
        if (source !== next || next.readyState !== 2) return
        closeSource()
        scheduleReopen()
      }
      next.onmessage = (event: MessageEvent<string>) => {
        if (source !== next || !options.authenticated()) return
        const reason = parsePlayerMatchmakerSignal(event.data)
        if (!reason) return
        options.onSignal({ reason, observedAt: now() })
      }
      source = next
    } catch {
      // Foreground REST polling and lifecycle reconciliation remain fallback.
    }
  }

  function closeSource(): void {
    const current = source
    source = null
    if (!current) return
    current.onmessage = null
    current.onopen = null
    current.onerror = null
    current.close()
  }

  function scheduleReopen(): void {
    if (
      reopenTimer !== undefined ||
      disposed ||
      !running ||
      suspended ||
      !options.authenticated()
    ) {
      return
    }
    const delays = [5_000, 15_000, 30_000, 60_000] as const
    const delay = delays[Math.min(reopenAttempt, delays.length - 1)]
    reopenAttempt += 1
    reopenTimer = schedule(() => {
      reopenTimer = undefined
      openSource()
    }, delay)
  }

  function cancelReopen(): void {
    if (reopenTimer === undefined) return
    cancel(reopenTimer)
    reopenTimer = undefined
  }

  function start(): void {
    running = true
    suspended = false
    cancelReopen()
    openSource()
  }

  function stop(): void {
    running = false
    suspended = false
    cancelReopen()
    reopenAttempt = 0
    closeSource()
  }

  function dispose(): void {
    if (disposed) return
    disposed = true
    cancelReopen()
    closeSource()
    visibilityTarget.removeEventListener('freeze', handleFreeze)
    visibilityTarget.removeEventListener('resume', handleResume)
    visibilityTarget.removeEventListener('visibilitychange', handleVisibilityChange)
    pageTarget.removeEventListener('pagehide', handlePageHide)
    pageTarget.removeEventListener('pageshow', handlePageShow)
  }

  return { start, stop, dispose }
}

export function parsePlayerMatchmakerSignal(
  raw: string,
): PlayerMatchmakerSignalReason | null {
  try {
    const envelope = JSON.parse(raw) as {
      event?: unknown
      data?: { refresh?: unknown; reason?: unknown }
    }
    if (!envelope.data || envelope.data.refresh !== true) return null
    if (envelope.event === 'matchmaker.snapshot') return 'snapshot'
    if (
      envelope.event !== 'matchmaker.changed' ||
      typeof envelope.data.reason !== 'string'
    ) {
      return null
    }
    return isPlayerMatchmakerSignalReason(envelope.data.reason)
      ? envelope.data.reason
      : null
  } catch {
    return null
  }
}

function isPlayerMatchmakerSignalReason(
  value: string,
): value is typeof playerMatchmakerSignalReasons[number] {
  return (playerMatchmakerSignalReasons as readonly string[]).includes(value)
}
